/**
 * FeatureTilesSection.jsx
 * Renders the three named benefit tiles required by AC 1.1.1.
 *
 * AC 1.1.1 – requirements met:
 *   - "Understand Your Risk"  - tile 1
 *   - "Real-Time Support"     - tile 2
 *   - "Track Your Progress"   - tile 3
 *   - All three visible above the fold on desktop/tablet (inline with hero)
 *     On mobile the tiles scroll below the hero (acceptable on small screens)
 *
 * The TILES_DATA array is kept separate from JSX for easy extensibility —
 * new tiles can be added without touching the render logic.
 *
 * Team: TA37 - The Coders' Assembly
 */

import React, { useEffect, useRef } from 'react';
import FeatureTile from '../common/FeatureTile';
import './FeatureTilesSection.css';

const TILES_DATA = [
  {
    id: 'tile-risk',
    icon: '🎯',
    title: 'Understand Your Risk',
    description:
      'Get a personalised relapse risk score based on your current mood, environment, and triggers.',
  },
  {
    id: 'tile-support',
    icon: '⚡',
    title: 'Real-Time Support',
    description:
      'Receive targeted coping strategies and resources the moment you need them, matched to your specific situation.',
  },
  {
    id: 'tile-progress',
    icon: '📈',
    title: 'Track Your Progress',
    description:
      'See how your risk patterns change over time and celebrate every smoke-free milestone along the way.',
  },
];

const FeatureTilesSection = () => {
  const tilesRef = useRef(null);

  useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('feature-tile--visible');
        } else {
          entry.target.classList.remove('feature-tile--visible');
        }
      });
    },
    { threshold: 0.15 }
  );

  const tiles = tilesRef.current?.querySelectorAll('.feature-tile');
  tiles?.forEach((tile) => observer.observe(tile));

  return () => observer.disconnect();
}, []);

  return (
    <section
      className="feature-tiles-section"
      aria-label="Platform features and benefits"
      ref={tilesRef}
    >
      <h2 className="feature-tiles-section__heading">
        Everything you need to stay on track
      </h2>

      <div className="feature-tiles-section__grid">
        {TILES_DATA.map((tile) => (
          <FeatureTile
            key={tile.id}
            icon={tile.icon}
            title={tile.title}
            description={tile.description}
          />
        ))}
      </div>
    </section>
  );
};

export default FeatureTilesSection;